<template>
  <div id="sale-report">
    <div class="container">

      <TipBox info :title="$t('tips.stats.title')">
        <span v-html="$t('tips.stats.description')"></span>
      </TipBox>

      <Panel class="configurations" :title="$t('sale_report.title')" collapsible>
        <div class="columns">

          <div class="column">
            <label class="subtitle">{{ $t('filters') }}</label>
            <div class="group">
              <label>{{ $t('start') }}</label>
              <datetime v-model="parameters.start" :use12-hour="hourFormat == '12'" type="datetime" value-zone="local"
                input-id="p_start" :input-class="{ invalid: $validate.error('parameters.start') }"
                input-style="width: 100%" />
            </div>
            <div class="group">
              <label>{{ $t('end') }}</label>
              <datetime v-model="parameters.end" :use12-hour="hourFormat == '12'" type="datetime" value-zone="local"
                input-id="p_end" :input-class="{ invalid: $validate.error('parameters.end') }"
                input-style="width: 100%" />
            </div>

            <div class="group">
              <label>{{ $t('branch.company') }}</label>
              <select v-model="parameters.company">
                <option :value="null" v-if="companySelect.length > 1">{{ $t('all_f') }}</option>
                <option :value="c.id" v-for="c of companySelect" :key="`c_${c.id}`">{{ c.name }}</option>
              </select>
            </div>

            <div class="group">
              <label>{{ $t('statistics.column.deliveryMethod') }}</label>
              <select v-model="parameters.deliveryMethod">
                <option :value="null">{{ $t('all_f') }}</option>
                <option :value="m.id" v-for="m of parameters.supportedMethods" :key="`dm_${m.id}`">{{ $t(m.transKey) }}
                </option>
              </select>
            </div>

            <div class="group">
              <label>{{ $t('origin') }}</label>
              <select v-model="parameters.appName">
                <option :value="null">{{ $t('all_f') }}</option>
                <option :value="app.id" v-for="app of parameters.apps" :key="`app_${app.id}`">{{ $t(app.transKey) }}
                </option>
              </select>
            </div>
          </div>

          <div class="column">
            <div class="group" v-if="parameters.deliveryMethod === 'takeout'">
              <div class="subtitle">{{ $t('sale_report.takeout_sales') }}</div>
              <label>{{ $t('sale_report.sale_type') }}</label>
              <select v-model="parameters.paymentOnly">
                <option :value="null">{{ $t('sale_report.all_sales') }}</option>
                <option :value="true">{{ $t('sale_report.payment_only_sales') }}</option>
                <option :value="false">{{ $t('sale_report.not_payment_only_sales') }}</option>
              </select>
              <br>
            </div>
            <div class="group">
              <label class="subtitle">{{ $t('sale_report.selection') }}</label>
              <div class="group" v-for="r of parameters.reportChoice" :key="`reportChoice-${r}`">
                <input :id="`${r}_chk`" type="checkbox" :value="r" v-model="parameters.reportSelected">
                <label :for="`${r}_chk`">{{ $t(`sale_report.types.${r}`) }}</label>
              </div>
            </div>
          </div>

        </div>

        <button class="btn btn-primary" @click="generate">{{ $t('generate') }}</button>
      </Panel>

      <template v-if="ready">
        <Panel :title="$t(`sale_report.types.${i}`)" v-for="(t, i) of reports" :key="`report-${i}`"
          v-if="nbTables > 0">
          <RawTable :keyValueHeader="t.options.filters" :columns="t.columns" :rows="t.data" />
        </Panel>
      </template>

    </div>
  </div>
</template>

<script>
  import RawTable from '@/components/RawTable.vue';
  import moment from "moment-timezone";
  import { Core } from '@/core/Core';

  export default {
    name: "SaleReport",

    components: { RawTable },

    data() {
      return {
        ready: false,
        reports: [],
        companySelect: [],

        tab: 0,
        parameters: {
          branch: this.$route.query.branch || null,
          company: this.$route.query.company || null,
          start: moment().startOf('year').toISOString(),
          end: moment().endOf('year').toISOString(),
          paymentOnly: null,
          deliveryMethod: null,
          appName: null,
          reportChoice: [
            'summary',
            'byCompany',
            'byAppName',
            'byDeliveryMethod',
            'byYear',
            'byMonth',
            'byWeek',
            'byWeek_day',
            'byHour',
            'byWeek_day_hour',
            'byItem',
            'byCategory',
            'byPromotion'
          ],
          reportSelected: [
            'summary'
          ],
          supportedMethods: [
            { id: 'delivery', transKey: 'deliveryMethod.delivery' },
            { id: 'takeout', transKey: 'deliveryMethod.takeout' },
            { id: 'inPlace', transKey: 'deliveryMethod.inPlace' },
            { id: 'catering', transKey: 'deliveryMethod.catering' },
            { id: 'hotel', transKey: 'deliveryMethod.hotel' },
          ],
          apps: [
            { id: 'ishopv3-web', transKey: 'appName.ishopv3-web' },
            { id: 'ishopv3-app', transKey: 'appName.ishopv3-app' },
            { id: 'ishopv3-kiosk', transKey: 'appName.ishopv3-kiosk' },
            { id: 'kiosk-backbone', transKey: 'appName.kiosk-backbone' },
            { id: 'ishopv3-backend', transKey: 'appName.ishopv3-backend' },
          ]
        }
      }
    },

    computed: {
      nbTables() {
        return Object.keys(this.reports).length;
      },
      hourFormat() {
        if (this.$store.state.contextCompany) {
          return this.$store.state.contextCompany.hourFormat;
        }
        return "24";
      },
    },

    validate: {
      "parameters.start": {
        required: true
      },
      "parameters.end": {
        required: true
      }
    },

    watch: {
      'parameters.deliveryMethod'() {
        this.parameters.paymentOnly = null;
      },
      'parameters.branch': {
        immediate: true,
        async handler(branch) {
          let filterCompanyIds = this.$store.getters.loggedUserHasBranchAdmin ? undefined : this.parameters.company;
          this.companySelect = await Core.request('GET', '/api/entity/company/select', { branch, id: filterCompanyIds });
        }
      }
    },

    methods: {
      fixDateTime(datetime) {
        return datetime.replace('T', ' ').substr(0, 19)
      },
      generate() {
        let filters = {
          filters: {
            branch: this.parameters.company ? undefined : this.parameters.branch,
            company: this.parameters.company ? this.parameters.company : undefined,
            start: this.parameters.start ? this.fixDateTime(this.parameters.start) : undefined,
            end: this.parameters.end ? this.fixDateTime(this.parameters.end) : undefined,
            paymentOnly: this.parameters.paymentOnly !== null ? this.parameters.paymentOnly : undefined,
            deliveryMethod: this.parameters.deliveryMethod || undefined,
            appName: this.parameters.appName || undefined
          }
        };

        /* Construct requests */
        let requests = this.parameters.reportSelected.map(r => {
          let parameters = Object.assign({}, filters);
          parameters.order = ['byWeek_day', 'byWeek_day_hour', 'byHour'].includes(r) ? 'asc' : 'desc';

          if (r !== 'summary') {
            let groupBy = r.replace('by', '');
            groupBy = groupBy.slice(0, 1).toLowerCase() + groupBy.slice(1);
            parameters.groupBy = [groupBy];
          }
          return Core.request('POST', '/api/stats-builder/sales', parameters)
        });

        this.$store.commit("showSpinner", true);
        Promise
          .all(requests)
          .then(responses => {
            this.reports = {};
            for (let [index, reportName] of this.parameters.reportSelected.entries()) {
              this.reports[reportName] = responses[index];
            }
            this.tab = this.parameters.reportSelected[0];
            this.ready = true;
          })
          .finally(() => {
            this.$store.commit("showSpinner", false)
          });
      }
    }
  }
</script>

<style lang="scss" scoped>
  .summary {
    width: 200px;
  }

  @media print {
    .configurations {
      display: none !important;
    }

    .tabs {
      display: none !important;
    }
  }
</style>